import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ClientOnly } from 'vite-react-ssg';
import { brand } from '../config/brand';

const TRUNCATE_AT = 200;

interface Review {
  author: string;
  photoUrl?: string;
  rating: number;
  text: string;
  relativeTime: string;
}

interface ReviewsData {
  rating: number;
  totalRatings: number;
  reviews: Review[];
  updatedAt: string;
}

function GoogleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}

function StarIcon({ filled }: { filled: boolean }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
      fill={filled ? '#FBBC04' : 'none'}
      stroke={filled ? '#FBBC04' : 'var(--color-border-strong)'}
      strokeWidth="1.5"
    >
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

function Stars({ rating, size = 'sm' }: { rating: number; size?: 'sm' | 'lg' }) {
  return (
    <span className={`reviews__stars reviews__stars--${size}`} aria-label={`${rating} sobre 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <StarIcon key={n} filled={n <= Math.round(rating)} />
      ))}
    </span>
  );
}

function ReviewCard({ review }: { review: Review }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const needsTruncation = review.text.length > TRUNCATE_AT;
  const displayText =
    needsTruncation && !expanded ? review.text.slice(0, TRUNCATE_AT).trimEnd() + '…' : review.text;

  return (
    <li className="review-card">
      <div className="review-card__meta">
        {review.photoUrl ? (
          <img
            src={review.photoUrl}
            alt={review.author}
            className="review-card__avatar"
            width="40"
            height="40"
            loading="lazy"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className="review-card__avatar review-card__avatar--initial" aria-hidden="true">
            {review.author.charAt(0).toUpperCase()}
          </div>
        )}
        <div className="review-card__author-info">
          <p className="review-card__name">{review.author}</p>
          <Stars rating={review.rating} />
        </div>
      </div>
      <p className="review-card__time">{review.relativeTime}</p>
      {review.text && (
        <div className="review-card__body">
          <p className="review-card__text">{displayText}</p>
          {needsTruncation && (
            <button
              className="review-card__toggle"
              onClick={() => setExpanded((e) => !e)}
              aria-expanded={expanded}
            >
              {expanded ? t('reviews.readLess') : t('reviews.readMore')}
            </button>
          )}
        </div>
      )}
    </li>
  );
}

function ReviewsInner() {
  const { t } = useTranslation();
  const [data, setData] = useState<ReviewsData | null>(null);

  useEffect(() => {
    fetch('/reviews.json')
      .then((r) => (r.ok ? (r.json() as Promise<ReviewsData>) : Promise.resolve(null)))
      .then(setData)
      .catch(() => {
        /* silent fail — section simply won't render */
      });
  }, []);

  if (!data || data.reviews.length === 0) return null;

  // Review structured data, attached to the existing LocalBusiness node by @id so
  // it backs the aggregateRating with the same reviews shown to visitors.
  const reviewSchema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${brand.url}/#organization`,
    review: data.reviews
      .filter((r) => r.text)
      .map((r) => ({
        '@type': 'Review',
        author: { '@type': 'Person', name: r.author },
        reviewRating: {
          '@type': 'Rating',
          ratingValue: r.rating,
          bestRating: 5,
          worstRating: 1,
        },
        reviewBody: r.text,
      })),
  };

  return (
    <section className="reviews-section" aria-labelledby="reviews-title">
      <script type="application/ld+json">{JSON.stringify(reviewSchema)}</script>
      <div className="reviews container">
        <header className="reviews__header">
          <div className="reviews__badge">
            <GoogleIcon />
            <span id="reviews-title" className="reviews__badge-label">
              Google Reviews
            </span>
          </div>
          <div className="reviews__summary">
            <span className="reviews__avg" aria-hidden="true">
              {data.rating.toFixed(1)}
            </span>
            <Stars rating={data.rating} size="lg" />
            <a
              href={brand.googleMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="reviews__total"
            >
              {data.totalRatings.toLocaleString()} reseñas
            </a>
          </div>
        </header>

        <ul className="reviews__list" aria-label="Reseñas de clientes">
          {data.reviews.map((review, i) => (
            <ReviewCard key={i} review={review} />
          ))}
        </ul>

        <a
          href={brand.googleMapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="reviews__cta"
        >
          {t('reviews.viewAll')}
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
            <polyline points="15 3 21 3 21 9" />
            <line x1="10" y1="14" x2="21" y2="3" />
          </svg>
        </a>
      </div>
    </section>
  );
}

export function ReviewsSection() {
  return <ClientOnly>{() => <ReviewsInner />}</ClientOnly>;
}
